import React, { useState } from 'react';
import { COLORS, CFF_LOGO, Sidebar, Footer, sharedStyles } from './shared';
import ReportFormatter from './components/ReportFormatter';
import DisputeStrategy from './components/DisputeStrategy';
import DisputeLetters  from './components/DisputeLetters';

export default function App() {
  const [path, setPath]                     = useState(null); // null=home, 'full'=3-step, 'letters'=letter shortcut
  const [currentStep, setCurrentStep]       = useState(0);
  const [formattedReport, setFormattedReport] = useState(null);
  const [strategyData, setStrategyData]     = useState(null);
  const [selectedActions, setSelectedActions] = useState([]);
  const [pastedStrategy, setPastedStrategy] = useState('');
  const [parsedActions, setParsedActions]   = useState(null);
  const [parsing, setParsing]               = useState(false);

  const { callClaude } = require('./shared');

  const steps = {
    0: true,
    1: !!formattedReport,
    2: !!strategyData,
  };

  const handleFormatterComplete = (report) => {
    setFormattedReport(report);
    setCurrentStep(1);
  };

  const handleStrategyComplete = ({ strategy, selectedActions }) => {
    setStrategyData(strategy);
    setSelectedActions(selectedActions);
    setCurrentStep(2);
  };

  const handleParseStrategy = async () => {
    if (!pastedStrategy.trim()) return;
    setParsing(true);
    try {
      const { callClaude } = await import('./shared');
      const prompt = `Parse this dispute strategy and return ONLY a JSON array of actions, no markdown, no backticks:
[{"id":"1","priority":"HIGH","category":"","creditor":"","bureau":"","issue":"","action":"","letterType":"dispute","estimatedImpact":"","timeframe":"30-45 days","fcraGrounds":""}]

Strategy text:
${pastedStrategy}`;
      const text = await callClaude([{ role:'user', content:prompt }]);
      const clean = text.replace(/```json|```/g,'').trim();
      const actions = JSON.parse(clean);
      setParsedActions(actions);
      setSelectedActions(actions);
      setStrategyData({ priorityActions: actions });
      setPath('letters-ready');
    } catch(err) {
      alert('Could not parse strategy. Please check the format and try again.');
    } finally {
      setParsing(false);
    }
  };

  // HOME SCREEN
  if (!path) {
    return (
      <div style={{ ...sharedStyles.root, justifyContent:'center', alignItems:'center' }}>
        <div style={{ background:'#1B3A6B', padding:'8px 24px', textAlign:'center', fontSize:'12px', color:'rgba(255,255,255,0.9)', width:'100%' }}>
          ⏳ Please be patient — the full analysis process takes approximately 3 minutes to complete. Do not refresh the page.
        </div>
        <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:40, background: COLORS.bg }}>
          <img src={CFF_LOGO} alt="Coleman Financial Firm" style={{ height:60, marginBottom:24 }} />
          <h1 style={{ fontSize:'24px', fontWeight:300, letterSpacing:'0.12em', color: COLORS.text, textTransform:'uppercase', marginBottom:8, textAlign:'center' }}>
            CFF Credit <span style={{ color: COLORS.gold, fontWeight:700 }}>Restoration Tool</span>
          </h1>
          <p style={{ color: COLORS.muted, fontSize:'13px', marginBottom:40, textAlign:'center', maxWidth:480 }}>
            Choose your path below — run a full credit analysis or jump straight to generating dispute letters.
          </p>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, maxWidth:700, width:'100%' }}>
            {/* PATH 1 */}
            <div
              onClick={() => setPath('full')}
              style={{ background:'#fff', border:`2px solid ${COLORS.border}`, borderRadius:12, padding:28, cursor:'pointer', textAlign:'center', transition:'all 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = COLORS.gold}
              onMouseLeave={e => e.currentTarget.style.borderColor = COLORS.border}
            >
              <div style={{ fontSize:'36px', marginBottom:12 }}>📊</div>
              <div style={{ color: COLORS.text, fontWeight:700, fontSize:'15px', marginBottom:8 }}>Full Credit Analysis</div>
              <div style={{ color: COLORS.muted, fontSize:'12px', lineHeight:1.6 }}>
                Paste your credit report and we'll format it, build your dispute strategy, and generate personalized letters — all in one flow.
              </div>
              <div style={{ marginTop:16, padding:'10px', background: COLORS.gold, borderRadius:8, color:'#fff', fontSize:'13px', fontWeight:600 }}>
                Start Full Analysis →
              </div>
            </div>
            {/* PATH 2 */}
            <div
              onClick={() => setPath('letters')}
              style={{ background:'#fff', border:`2px solid ${COLORS.border}`, borderRadius:12, padding:28, cursor:'pointer', textAlign:'center', transition:'all 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = COLORS.gold}
              onMouseLeave={e => e.currentTarget.style.borderColor = COLORS.border}
            >
              <div style={{ fontSize:'36px', marginBottom:12 }}>✉️</div>
              <div style={{ color: COLORS.text, fontWeight:700, fontSize:'15px', marginBottom:8 }}>Generate Letters Only</div>
              <div style={{ color: COLORS.muted, fontSize:'12px', lineHeight:1.6 }}>
                Already have your dispute strategy? Paste it here and we'll generate your personalized dispute letters instantly.
              </div>
              <div style={{ marginTop:16, padding:'10px', background:'#1B3A6B', borderRadius:8, color:'#fff', fontSize:'13px', fontWeight:600 }}>
                Go to Letter Generator →
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // LETTER SHORTCUT SCREEN
  if (path === 'letters') {
    return (
      <div style={sharedStyles.root}>
        <div style={{ background:'#1B3A6B', padding:'8px 24px', textAlign:'center', fontSize:'12px', color:'rgba(255,255,255,0.9)' }}>
          ⏳ Please be patient — letter generation takes 1-2 minutes per letter. Do not refresh the page.
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:'32px', background: COLORS.bg }}>
          <div style={{ maxWidth:700, margin:'0 auto' }}>
            <button onClick={() => setPath(null)} style={{ background:'none', border:'none', color: COLORS.muted, fontSize:'12px', cursor:'pointer', marginBottom:20 }}>← Back</button>
            <div style={sharedStyles.pageTitle}>GENERATE <span style={sharedStyles.pageTitleAccent}>LETTERS</span></div>
            <div style={sharedStyles.pageSubtitle}>Paste your existing dispute strategy below and we'll generate your letters.</div>
            <div style={{ marginTop:24, background:'#fff', border:`1px solid ${COLORS.border}`, borderRadius:10, padding:20 }}>
              <textarea
                style={{ width:'100%', minHeight:200, background:'#f8f9fb', border:`1px solid ${COLORS.border}`, borderRadius:7, color: COLORS.text, padding:16, fontSize:'12px', resize:'vertical', outline:'none', boxSizing:'border-box', lineHeight:1.7 }}
                placeholder="Paste your CFF dispute strategy here — include creditor names, bureaus, issues, and recommended actions..."
                value={pastedStrategy}
                onChange={e => setPastedStrategy(e.target.value)}
              />
              <button
                onClick={handleParseStrategy}
                disabled={parsing || pastedStrategy.trim().length < 20}
                style={{ width:'100%', marginTop:14, padding:'13px', background: parsing || pastedStrategy.trim().length < 20 ? '#f0f4fa' : COLORS.gold, border:'none', borderRadius:8, color: parsing || pastedStrategy.trim().length < 20 ? COLORS.dim : '#fff', fontSize:'14px', fontWeight:700, cursor: parsing ? 'wait' : 'pointer' }}
              >
                {parsing ? '⏳ Parsing your strategy...' : '✨ Generate My Letters →'}
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // LETTERS READY — show DisputeLetters directly
  if (path === 'letters-ready') {
    return (
      <div style={sharedStyles.root}>
        <div style={{ background:'#1B3A6B', padding:'8px 24px', textAlign:'center', fontSize:'12px', color:'rgba(255,255,255,0.9)' }}>
          ⏳ Please be patient — letter generation takes 1-2 minutes per letter. Do not refresh the page.
        </div>
        <div style={sharedStyles.layout}>
          <div style={{ flex:1, overflowY:'auto' }}>
            <DisputeLetters
              report={formattedReport || {}}
              strategy={strategyData}
              selectedActions={selectedActions}
            />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // FULL 3-STEP FLOW
  return (
    <div style={sharedStyles.root}>
      <div style={{ background:'#1B3A6B', padding:'8px 24px', textAlign:'center', fontSize:'12px', color:'rgba(255,255,255,0.9)' }}>
        ⏳ Please be patient — the full analysis process takes approximately 3 minutes to complete. Do not refresh the page.
      </div>
      {/* Step Progress Bar */}
      <div style={{ background:'#1B3A6B', borderBottom:'1px solid #1B3A6B', padding:'8px 24px', display:'flex', alignItems:'center', gap:0 }}>
        {[
          { num:1, label:'Format Report',    step:0 },
          { num:2, label:'Build Strategy',   step:1 },
          { num:3, label:'Generate Letters', step:2 },
        ].map((item, i) => {
          const done   = currentStep > item.step;
          const active = currentStep === item.step;
          const locked = !steps[item.step];
          return (
            <React.Fragment key={item.step}>
              <div style={{ display:'flex', alignItems:'center', gap:8, opacity: locked?0.4:1 }}>
                <div style={{
                  width:26, height:26, borderRadius:'50%',
                  background: done?'#2e7d32':active?'#C9A84C':'#e8dfc8',
                  border:`2px solid ${done?'#2e7d32':active?'#C9A84C':'#c8b99a'}`,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:'11px', fontWeight:700,
                  color: (done||active)?'#fff':'#8a7a5a',
                }}>
                  {done ? '✓' : item.num}
                </div>
                <span style={{ fontSize:'12px', fontWeight: active?600:400, color: done?'#2e7d32':active?'#C9A84C':'rgba(255,255,255,0.7)' }}>
                  {item.label}
                </span>
              </div>
              {i < 2 && (
                <div style={{ flex:1, height:2, background: currentStep>item.step?'#2e7d32':'rgba(255,255,255,0.2)', margin:'0 12px', maxWidth:80 }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      <div style={sharedStyles.layout}>
        <Sidebar steps={steps} currentStep={currentStep} onStepClick={setCurrentStep} />
        <div style={{ flex:1, overflowY:'auto' }}>
          {currentStep === 0 && (
            <ReportFormatter onComplete={handleFormatterComplete} />
          )}
          {currentStep === 1 && formattedReport && (
            <DisputeStrategy
              report={formattedReport}
              onComplete={handleStrategyComplete}
            />
          )}
          {currentStep === 2 && strategyData && (
            <DisputeLetters
              report={formattedReport}
              strategy={strategyData}
              selectedActions={selectedActions}
            />
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
